import { Controller } from "@hotwired/stimulus";

// Combined Save + Add-to-list popover (Pinterest/Spotify-style).
//
// State is the source of truth:
//   savedInfluencerId  → null when not saved; numeric when saved
//   memberships        → Map<list_id, membership_id> for current creator
//
// All actions optimistically update the UI then sync via JSON. Rollback on failure.
export default class extends Controller {
  static targets = [
    "panel",
    "trigger",
    "saveLabel",
    "saveIcon",
    "search",
    "list",
    "row",
    "createButton",
    "createName",
    "footer",
  ];
  static values = {
    influencerId: Number,
    savedInfluencerId: Number,
    saved: Boolean,
    memberships: Object, // { "<list_id>": <membership_id> }
    // Surfaces where the creator has no Influencer record to key off (an
    // assignment row, where the collaborator may be an email-only invite)
    // supply their own save endpoint plus the params that identify the
    // creator. Same JSON contract: { saved, saved_influencer_id, ... }.
    saveUrl: String,
    savePayload: Object,
    saveLabel: { type: String, default: "Save" },
    savedLabel: { type: String, default: "Saved" },
    // Class added to the trigger once saved. "" keeps the button quiet in
    // both states, for surfaces where a solid button would outshout the
    // page's real CTA; the filled heart and the label still carry the state.
    savedTriggerClass: { type: String, default: "primary" },
    // Inline variant (saved_influencers/_save_and_list_inline): rendered as a
    // plain list rather than a popover, so rows use the shared
    // .creator-lists-sidebar markup and there is no trigger to toggle.
    inline: Boolean,
  };

  connect() {
    this._onDocClick = (e) => {
      if (!this.element.contains(e.target)) this.close();
    };
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick, true);
  }

  togglePanel(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.panelTarget.hidden) {
      // Pinterest-style: clicking "Save" when not yet saved saves immediately
      // and opens the popover so the user can optionally pick a list.
      if (!this.savedValue) this._save();
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.panelTarget.hidden = false;
    document.addEventListener("click", this._onDocClick, true);
    if (this.hasSearchTarget) {
      setTimeout(() => this.searchTarget.focus(), 0);
    }
  }

  close() {
    this.panelTarget.hidden = true;
    document.removeEventListener("click", this._onDocClick, true);
    if (this.hasSearchTarget) {
      this.searchTarget.value = "";
      this._filter("");
    }
  }

  // Toggle save state via the heart-row at the top of the popover.
  async toggleSave(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.dataset.busy === "true") return;
    this._setButtonBusy(button, true);
    try {
      if (this.savedValue) {
        await this._unsave();
      } else {
        await this._save();
      }
    } finally {
      this._setButtonBusy(button, false);
    }
  }

  // Toggle a list-membership row.
  async toggleList(event) {
    event.preventDefault();
    const row = event.currentTarget;
    // Saving can take a beat, and the row looks unchanged until it lands, so
    // without this a second click fires a second request.
    if (row.dataset.busy === "true") return;
    const listId = Number(row.dataset.listId);
    const isMember = row.dataset.member === "true";

    this._setRowBusy(row, true);
    try {
      if (isMember) {
        await this._removeFromList(listId);
      } else {
        await this._addToList(listId);
      }
    } finally {
      this._setRowBusy(row, false);
    }
  }

  // Filter list rows by search text. Show "Create '<query>'" when no exact match.
  filterLists() {
    const q = this.searchTarget.value.trim();
    this._filter(q);
  }

  _filter(q) {
    const lower = q.toLowerCase();
    let exact = false;
    this.rowTargets.forEach((row) => {
      const name = (row.dataset.listName || "").toLowerCase();
      const match = !lower || name.includes(lower);
      row.hidden = !match;
      if (name === lower) exact = true;
    });
    if (this.hasCreateButtonTarget) {
      const showCreate = q.length > 0 && !exact;
      this.createButtonTarget.hidden = !showCreate;
      if (showCreate && this.hasCreateNameTarget) {
        this.createNameTarget.textContent = q;
      }
    }
  }

  // Create a new list with the typed name, then add this creator to it.
  async createList(event) {
    event.preventDefault();
    const name = this.searchTarget.value.trim();
    if (!name) return;
    if (
      this.hasCreateButtonTarget &&
      this.createButtonTarget.dataset.busy === "true"
    )
      return;

    // Creating is two round trips (make the list, then file the creator into
    // it), so this is the slowest thing in here and the one most in need of
    // saying something is happening.
    if (this.hasCreateButtonTarget)
      this._setButtonBusy(this.createButtonTarget, true);
    try {
      await this._createListAndAdd(name);
    } finally {
      if (this.hasCreateButtonTarget)
        this._setButtonBusy(this.createButtonTarget, false);
    }
  }

  async _createListAndAdd(name) {
    const res = await this._postJSON("/creator_lists", {
      creator_list: { name },
    });
    if (!res?.id) return;

    // Append to the list dynamically and mark as member after add. The popover
    // keeps its Create row inside the list, the inline variant doesn't, so
    // only insert before it when it's actually a sibling.
    const li = this._buildRow(res.id, res.name, false);
    const anchor =
      this.hasCreateButtonTarget &&
      this.createButtonTarget.parentElement === this.listTarget
        ? this.createButtonTarget
        : null;
    if (anchor) this.listTarget.insertBefore(li, anchor);
    else this.listTarget.appendChild(li);

    await this._addToList(res.id);

    this.searchTarget.value = "";
    this._filter("");
    this.searchTarget.focus();
  }

  // ---- core save/list mutations ----------------------------------------

  async _save() {
    const res = this.saveUrlValue
      ? await this._postJSON(this.saveUrlValue, { ...this.savePayloadValue })
      : await this._postJSON("/saved_influencers", {
          saved_influencer: { influencer_id: this.influencerIdValue },
          save_toggle_context: "row",
        });
    if (res?.saved) {
      this.savedValue = true;
      this.savedInfluencerIdValue = res.saved_influencer_id;
      this._renderSavedState();
    }
  }

  async _unsave() {
    if (!this.savedInfluencerIdValue) return;
    const res = await this._fetchJSON(
      `/saved_influencers/${this.savedInfluencerIdValue}`,
      "DELETE"
    );
    if (res?.saved === false) {
      this.savedValue = false;
      this.savedInfluencerIdValue = 0;
      // Removing the saved record cascades to all memberships server-side.
      this.rowTargets.forEach((r) => this._setMemberState(r, false, null));
      this._renderSavedState();
    }
  }

  async _addToList(listId) {
    let res;
    if (!this.savedValue) {
      // Save and add in one shot
      res = this.saveUrlValue
        ? await this._postJSON(this.saveUrlValue, {
            ...this.savePayloadValue,
            creator_list_id: listId,
          })
        : await this._postJSON("/discovery/save_and_add_to_list", {
            creator_list_id: listId,
            saved_influencer: { influencer_id: this.influencerIdValue },
          });
      if (res?.saved) {
        this.savedValue = true;
        this.savedInfluencerIdValue = res.saved_influencer_id;
        this._renderSavedState();
      }
    } else {
      res = await this._postJSON(
        `/creator_lists/${listId}/creator_list_memberships`,
        {
          saved_influencer_id: this.savedInfluencerIdValue,
        }
      );
    }
    if (res) {
      const row = this._rowFor(listId);
      if (row) this._setMemberState(row, true, res.membership_id);
    }
  }

  async _removeFromList(listId) {
    const row = this._rowFor(listId);
    const membershipId = row?.dataset.membershipId;
    if (!membershipId) return;
    const res = await this._fetchJSON(
      `/creator_lists/${listId}/creator_list_memberships/${membershipId}`,
      "DELETE"
    );
    if (res?.status === "removed") {
      this._setMemberState(row, false, null);
    }
  }

  // ---- DOM helpers -----------------------------------------------------

  _renderSavedState() {
    // Trigger button label + filled state
    if (this.hasTriggerTarget) {
      const triggerLabel = this.triggerTarget.querySelector("span");
      if (triggerLabel) {
        triggerLabel.textContent = this.savedValue
          ? this.savedLabelValue
          : this.saveLabelValue;
      }
      if (this.savedTriggerClassValue) {
        this.triggerTarget.classList.toggle(
          this.savedTriggerClassValue,
          this.savedValue
        );
      }
    }
    // Heart icon outline ↔ filled
    this.saveIconTargets.forEach((icon) => {
      icon.classList.toggle("outline", !this.savedValue);
    });
    // Footer (Remove from Saved) — only meaningful once saved.
    if (this.hasFooterTarget) {
      this.footerTarget.hidden = !this.savedValue;
    }
    this._syncSectionMeta();
    this.element.classList.toggle("is-saved", this.savedValue);
  }

  // The collapsed summary of the surrounding aside_section is rendered by the
  // server, so without this it still read "Not saved" while a list sat checked
  // right underneath it. Mirrors the view's wording exactly.
  _syncSectionMeta() {
    const meta = this.element
      .closest("details.aside-section")
      ?.querySelector(".aside-section__meta");
    if (!meta) return;
    const n = this.rowTargets.filter((r) => r.dataset.member === "true").length;
    if (!this.savedValue) meta.textContent = "Not saved";
    else if (n === 0) meta.textContent = "Saved";
    else meta.textContent = `${n} ${n === 1 ? "list" : "lists"}`;
  }

  // Turn the row's checkmark INTO the spinner rather than adding one beside
  // it: the checkmark still occupies its slot while hidden (it fades via
  // opacity, not display), so a separate element sat next to it instead of
  // where the check goes. Same element means the two line up exactly.
  _setRowBusy(row, busy) {
    row.dataset.busy = busy ? "true" : "false";
    row.setAttribute("aria-busy", busy ? "true" : "false");

    const check = row.querySelector("[data-role='check']");
    if (!check) return;

    check.classList.toggle("check", !busy);
    check.classList.toggle("notched", busy);
    check.classList.toggle("circle", busy);
    check.classList.toggle("loading", busy);
    check.classList.toggle(
      "hidden-soft",
      !busy && row.dataset.member !== "true"
    );
  }

  _setButtonBusy(button, busy) {
    button.dataset.busy = busy ? "true" : "false";
    button.setAttribute("aria-busy", busy ? "true" : "false");
    button.classList.toggle("loading", busy);
    button.classList.toggle("disabled", busy);
  }

  _rowFor(listId) {
    return this.rowTargets.find(
      (r) => Number(r.dataset.listId) === Number(listId)
    );
  }

  _setMemberState(row, isMember, membershipId) {
    row.dataset.member = isMember ? "true" : "false";
    if (membershipId) row.dataset.membershipId = membershipId;
    else delete row.dataset.membershipId;
    const check = row.querySelector("[data-role='check']");
    if (check) check.classList.toggle("hidden-soft", !isMember);
    this._syncSectionMeta();
  }

  _buildRow(id, name, isMember) {
    const li = document.createElement("button");
    li.type = "button";
    li.className = this.inlineValue
      ? "creator-lists-sidebar__item creator-save-list__pick"
      : "creator-save-list__row";
    li.dataset.listId = id;
    li.dataset.listName = name.toLowerCase();
    li.dataset.member = isMember ? "true" : "false";
    li.dataset.creatorSaveListTarget = "row";
    li.dataset.action = "click->creator-save-list#toggleList";
    const nameClass = this.inlineValue
      ? "creator-lists-sidebar__name"
      : "creator-save-list__row-name";
    li.innerHTML =
      `<i class="icon list"></i>` +
      `<span class="${nameClass}"></span>` +
      `<i class="icon check creator-save-list__row-check ${
        isMember ? "" : "hidden-soft"
      }" data-role="check"></i>`;
    li.querySelector(`.${nameClass}`).textContent = name;
    return li;
  }

  // ---- network helpers -------------------------------------------------

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  async _postJSON(url, body) {
    return this._fetchJSON(url, "POST", body);
  }

  async _fetchJSON(url, method, body) {
    try {
      const opts = {
        method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this._csrf(),
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      };
      if (body) opts.body = JSON.stringify(body);
      const res = await fetch(url, opts);
      if (!res.ok) return null;
      const ct = res.headers.get("content-type") || "";
      return ct.includes("application/json") ? await res.json() : {};
    } catch {
      return null;
    }
  }
}
